import React, { useState, useCallback } from "react";
import { format } from "date-fns";
import { X, Check, Edit, Trash2, Filter, SortAsc, SortDesc, DollarSign, Calendar, AlertCircle, Download, Upload, Plus } from "lucide-react";
import Papa from 'papaparse';
import { supabase } from '../lib/supabase';

interface RecurringExpensesProps {
  companyId: string | null;
}

const RecurringExpenses: React.FC<RecurringExpensesProps> = ({ companyId }) => {
  const [recurringExpenses, setRecurringExpenses] = useState([]);
  
  // Load expenses on mount and when companyId changes
  React.useEffect(() => {
    if (companyId) {
      loadExpenses();
    }
  }, [companyId]);
  
  const loadExpenses = async () => {
    try {
      const { data, error } = await supabase
        .from('company_expenses')
        .select('*')
        .eq('company_id', companyId)
        .order('date', { ascending: false });

      if (error) throw error;
      setRecurringExpenses(data || []);
    } catch (err) {
      console.error('Error loading expenses:', err);
      setError('Failed to load expenses');
    }
  };

  const [availableLocations] = useState([
    'Overhead',
    'Morristown',
    'Bean Station',
    'New Tazewell',
    'Maryville',
    'Newport',
    'Rogersville',
    'Jefferson City'
  ]);

  // Existing state
  const [newExpense, setNewExpense] = useState({
    description: "",
    amount: 0,
    frequency: "monthly",
    category: "Operational",
    date: format(new Date(), "yyyy-MM-dd"),
    locations: {},
  });

  // New state for filtering and sorting
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterFrequency, setFilterFrequency] = useState("all");
  const [sortField, setSortField] = useState("date");
  const [sortDirection, setSortDirection] = useState("desc");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [locationAmount, setLocationAmount] = useState(0);
  const [dateRange, setDateRange] = useState(() => ({
    start: format(new Date(new Date().getFullYear(), new Date().getMonth(), 1), 'yyyy-MM-dd'),
    end: format(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0), 'yyyy-MM-dd')
  }));
  
  const [isEditingExpense, setIsEditingExpense] = useState(false);
  const [editingExpenseId, setEditingExpenseId] = useState(null);
  const [error, setError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  
  // Handle saving expense
  const handleSaveEdit = useCallback(async (expense) => {
    try {
      if (!companyId) {
        throw new Error('Please select a company first');
      }

      if (!expense.description) {
        throw new Error('Description is required');
      }

      if (!expense.amount || expense.amount <= 0) {
        throw new Error('Amount must be greater than 0');
      }

      const { data, error } = await supabase
        .from('company_expenses')
        .insert({
          company_id: companyId,
          description: expense.description,
          amount: expense.amount,
          frequency: expense.frequency,
          category: expense.category,
          date: expense.date,
          locations: expense.locations
        })
        .select()
        .single();

      if (error) throw error;

      setRecurringExpenses(prev => [...prev, data]);
      return data;
    } catch (err) {
      console.error('Error saving expense:', err);
      throw err;
    }
  }, [companyId]);

  // Handle deleting expense
  const handleDeleteExpense = useCallback(async (id) => {
    try {
      const { error } = await supabase
        .from('company_expenses')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setRecurringExpenses(prev => prev.filter(expense => expense.id !== id));
    } catch (err) {
      console.error('Error deleting expense:', err);
      throw err;
    }
  }, []);

  // Calculate summary statistics
  const summaryStats = React.useMemo(() => {
    if (!Array.isArray(recurringExpenses)) return {
      total: 0,
      monthly: 0,
      annual: 0,
      byCategory: {}
    };

    return {
      total: recurringExpenses.reduce((sum, exp) => sum + exp.amount, 0),
      monthly: recurringExpenses
        .filter(exp => exp.frequency === "monthly")
        .reduce((sum, exp) => sum + exp.amount, 0),
      annual: recurringExpenses
        .filter(exp => exp.frequency === "annual")
        .reduce((sum, exp) => sum + (exp.amount / 12), 0),
      byCategory: recurringExpenses.reduce((acc, exp) => ({
        ...acc,
        [exp.category]: (acc[exp.category] || 0) + exp.amount
      }), {})
    };
  }, [recurringExpenses]);

  // Filter and sort expenses
  const filteredExpenses = React.useMemo(() => {
    if (!Array.isArray(recurringExpenses)) return [];
    
    return recurringExpenses
      .filter(expense => {
        const categoryMatch = filterCategory === "all" || expense.category === filterCategory;
        const frequencyMatch = filterFrequency === "all" || expense.frequency === filterFrequency;
        const dateMatch = new Date(expense.date) >= new Date(dateRange.start) && 
                         new Date(expense.date) <= new Date(dateRange.end);
        return categoryMatch && frequencyMatch && dateMatch;
      })
      .sort((a, b) => {
        const direction = sortDirection === "asc" ? 1 : -1;
        if (sortField === "amount") return (a.amount - b.amount) * direction;
        if (sortField === "date") return (new Date(a.date) - new Date(b.date)) * direction;
        return a[sortField].localeCompare(b[sortField]) * direction;
      });
  }, [recurringExpenses, filterCategory, filterFrequency, dateRange, sortField, sortDirection]);

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          {error}
        </div>
      )}

      {saveSuccess && (
        <div className="mb-4 p-3 bg-green-100 border border-green-300 text-green-700 rounded-lg flex items-center">
          <Check className="h-5 w-5 mr-2" />
          Changes saved successfully!
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex items-center mb-2">
            <DollarSign className="h-5 w-5 text-green-500 mr-2" />
            <h3 className="text-sm font-medium text-gray-700">Total Monthly</h3>
          </div>
          <p className="text-2xl font-bold text-green-600">
            ${(
              summaryStats.monthly + 
              summaryStats.annual + 
              (recurringExpenses
                .filter(exp => exp.frequency === "weekly")
                .reduce((sum, exp) => sum + (exp.amount * 4), 0))
              ).toLocaleString()}
          </p>
        </div>
        
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex items-center mb-2">
            <Calendar className="h-5 w-5 text-blue-500 mr-2" />
            <h3 className="text-sm font-medium text-gray-700">Annual Total</h3>
          </div>
          <p className="text-2xl font-bold text-blue-600">
            ${(
              (summaryStats.monthly * 12) + 
              summaryStats.annual + 
              (recurringExpenses
                .filter(exp => exp.frequency === "weekly")
                .reduce((sum, exp) => sum + (exp.amount * 52), 0))
              ).toLocaleString()}
          </p>
        </div>
        
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex items-center mb-2">
            <Filter className="h-5 w-5 text-purple-500 mr-2" />
            <h3 className="text-sm font-medium text-gray-700">Active Expenses</h3>
          </div>
          <p className="text-2xl font-bold text-purple-600">
            {filteredExpenses.length}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <button
          onClick={() => setIsEditingExpense(true)}
          className="bg-[#ff8200] hover:bg-[#e67600] text-white px-4 py-2 rounded-lg flex items-center"
        >
          <Plus className="h-5 w-5 mr-2" />
          Add Expense
        </button>

        <label className="cursor-pointer bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center">
          <Upload className="h-5 w-5 mr-2" />
          Upload CSV
          <input
            type="file"
            accept=".csv"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                Papa.parse(file, {
                  complete: (results) => {
                    const [headers, ...rows] = results.data;
                    const expenses = rows
                      .filter(row => row.length === headers.length)
                      .map(row => {
                        const expense: any = {};
                        headers.forEach((header, index) => {
                          const key = header.toLowerCase().trim();
                          expense[key] = row[index];
                        });
                        return {
                          ...expense,
                          amount: parseFloat(expense.amount) || 0,
                          date: expense.date || format(new Date(), 'yyyy-MM-dd'),
                          frequency: expense.frequency || 'monthly',
                          category: expense.category || 'Operational',
                          locations: expense.locations || {}
                        };
                      });
                      
                    // Handle the parsed expenses
                    Promise.all(expenses.map(expense => handleSaveEdit(expense)))
                      .then(() => {
                        setSaveSuccess(true); 
                        setTimeout(() => setSaveSuccess(false), 3000); 
                      })
                      .catch(err => {
                        setError('Failed to import expenses'); 
                        console.error('Import error:', err); 
                      });
                  },
                  header: true,
                  skipEmptyLines: true,
                  error: (err) => {
                    setError('Failed to parse CSV file');
                    console.error('Parse error:', err);
                      }
                });
              }
            }}
          />
        </label>

        <button
          onClick={() => {
            const csv = Papa.unparse(filteredExpenses);
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `recurring-expenses-${format(new Date(), 'yyyy-MM-dd')}.csv`;
            link.click();
          }}
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center"
        >
          <Download className="h-5 w-5 mr-2" />
          Export CSV
        </button>
      </div>
      
      {/* Expense List */}
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead>
            <tr className="bg-gray-50">
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Description
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Amount
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Frequency
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Category
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredExpenses.map((expense) => (
              <tr key={expense.id}>
                <td className="px-6 py-4 whitespace-nowrap">{expense.description}</td>
                <td className="px-6 py-4 whitespace-nowrap">${expense.amount.toLocaleString()}</td>
                <td className="px-6 py-4 whitespace-nowrap capitalize">{expense.frequency}</td>
                <td className="px-6 py-4 whitespace-nowrap">{expense.category}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {format(new Date(expense.date), 'MMM d, yyyy')}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => {
                        setEditingExpenseId(expense.id);
                        setNewExpense(expense);
                        setIsEditingExpense(true);
                      }}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <Edit className="h-5 w-5" />
                    </button>
                    {showDeleteConfirm === expense.id ? (
                      <>
                        <button
                          onClick={() => handleDeleteExpense(expense.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Check className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm(null)}
                          className="text-gray-600 hover:text-gray-800"
                        >
                          <X className="h-5 w-5" />
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => setShowDeleteConfirm(expense.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Expense Modal */}
      {isEditingExpense && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">
                {editingExpenseId ? 'Edit Expense' : 'Add New Expense'}
              </h2>
              <button
                onClick={() => {
                  setIsEditingExpense(false);
                  setEditingExpenseId(null);
                  setNewExpense({
                    description: "",
                    amount: 0,
                    frequency: "monthly",
                    category: "Operational",
                    date: format(new Date(), "yyyy-MM-dd"),
                    locations: {}
                  });
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description*
                </label>
                <input
                  type="text"
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#ff8200] focus:border-[#ff8200]"
                  value={newExpense.description}
                  onChange={(e) => setNewExpense({ ...newExpense, description: e.target.value })}
                  placeholder="e.g., Office Rent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount*
                </label>
                <div className="flex">
                  <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500">
                    $
                  </span>
                  <input
                    type="number"
                    className="flex-1 border border-gray-300 rounded-r-md px-3 py-2 focus:ring-2 focus:ring-[#ff8200] focus:border-[#ff8200]"
                    value={newExpense.amount}
                    onChange={(e) => setNewExpense({ ...newExpense, amount: parseFloat(e.target.value) || 0 })}
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Frequency*
                </label>
                <select
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#ff8200] focus:border-[#ff8200]"
                  value={newExpense.frequency}
                  onChange={(e) => setNewExpense({ ...newExpense, frequency: e.target.value })}
                >
                  <option value="monthly">Monthly</option>
                  <option value="weekly">Weekly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="annual">Annual</option>
                  <option value="one-time">One-time</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category*
                </label>
                <select
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#ff8200] focus:border-[#ff8200]"
                  value={newExpense.category}
                  onChange={(e) => setNewExpense({ ...newExpense, category: e.target.value })}
                >
                  <option value="Operational">Operational</option>
                  <option value="Marketing">Marketing</option>
                  <option value="Utilities">Utilities</option>
                  <option value="Insurance">Insurance</option>
                  <option value="Rent">Rent</option>
                  <option value="Software">Software</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date*
                </label>
                <input
                  type="date"
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#ff8200] focus:border-[#ff8200]"
                  value={newExpense.date}
                  onChange={(e) => setNewExpense({ ...newExpense, date: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Location Allocations
                </label>
                <div className="space-y-2">
                  {Object.entries(newExpense.locations).map(([location, amount]) => (
                    <div key={location} className="flex items-center gap-2">
                      <span className="flex-1">{location}</span>
                      <span>${amount}</span>
                      <button
                        onClick={() => {
                          const { [location]: _, ...rest } = newExpense.locations;
                          setNewExpense({ ...newExpense, locations: rest });
                        }}
                        className="text-red-600 hover:text-red-800"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  <div className="flex items-center gap-2">
                    <select
                      className="flex-1 border rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#ff8200] focus:border-[#ff8200]"
                      value={selectedLocation}
                      onChange={(e) => setSelectedLocation(e.target.value)}
                    >
                      <option value="">Select Location</option>
                      {availableLocations
                        .filter(loc => !Object.keys(newExpense.locations).includes(loc))
                        .map(location => (
                          <option key={location} value={location}>{location}</option>
                        ))}
                    </select>
                    <div className="flex-1">
                      <div className="flex">
                        <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500">
                          $
                        </span>
                        <input
                          type="number"
                          className="flex-1 border border-gray-300 rounded-r-md px-3 py-2 focus:ring-2 focus:ring-[#ff8200] focus:border-[#ff8200]"
                          value={locationAmount}
                          onChange={(e) => setLocationAmount(parseFloat(e.target.value) || 0)}
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        if (selectedLocation && locationAmount > 0) {
                          setNewExpense({
                            ...newExpense,
                            locations: {
                              ...newExpense.locations,
                              [selectedLocation]: locationAmount
                            }
                          });
                          setSelectedLocation("");
                          setLocationAmount(0);
                        }
                      }}
                      className="px-4 py-2 bg-[#ffead5] hover:bg-[#ffe0c1] text-[#ff8200] font-medium rounded-lg"
                    >
                      Add
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  setIsEditingExpense(false);
                  setEditingExpenseId(null);
                  setNewExpense({
                    description: "",
                    amount: 0,
                    frequency: "monthly",
                    category: "Operational",
                    date: format(new Date(), "yyyy-MM-dd"),
                    locations: {}
                  });
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleSaveEdit(newExpense)
                    .then(() => {
                      setIsEditingExpense(false);
                      setEditingExpenseId(null);
                      setNewExpense({
                        description: "",
                        amount: 0,
                        frequency: "monthly",
                        category: "Operational",
                        date: format(new Date(), "yyyy-MM-dd"),
                        locations: {}
                      });
                      setSaveSuccess(true);
                      setTimeout(() => setSaveSuccess(false), 3000);
                    })
                    .catch(err => {
                      setError('Failed to save expense');
                      console.error('Save error:', err);
                    });
                }}
                className="bg-[#ff8200] hover:bg-[#e67600] text-white px-4 py-2 rounded-lg"
              >
                {editingExpenseId ? 'Save Changes' : 'Add Expense'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export default RecurringExpenses